/**
 * BreakdownBar — renders breakdown tiles. Modes are decided by the backend
 * payload shape:
 *   • chart='horizontal_bar' → SingleMetricBar (horizontal OR vertical, optional
 *     preserve-order, optional pct value formatting).
 *   • chart='table' + display='cards' → MultiMetricCards (grid of stat cards).
 *   • chart='table' (default) → MultiMetricTable (sortable, honors m.no_total in
 *     the grand-total row).
 *   • chart='combo' → ComboFunnelChart (dual-axis: volume bars + drop-off %).
 */
import React, { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import {
  Table, TableContainer, Thead, Tbody, Tr, Th, Td, Text, HStack, Icon,
  SimpleGrid, Box, Stat, StatLabel, StatNumber,
} from '@chakra-ui/react';
import { FiChevronUp, FiChevronDown } from 'react-icons/fi';

import { PRIMARY_BG, PRIMARY_BG_HOVER } from '../../../theme/brand';
function fmtCompact(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const x = Number(n);
  if (Math.abs(x) >= 1e9) return (x / 1e9).toFixed(2) + 'B';
  if (Math.abs(x) >= 1e6) return (x / 1e6).toFixed(2) + 'M';
  if (Math.abs(x) >= 1e3) return (x / 1e3).toFixed(1) + 'K';
  return Number.isInteger(x) ? x.toLocaleString() : x.toFixed(2);
}

function fmtPct(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const x = Number(n);
  return `${x.toFixed(1)}%`;
}

function MultiMetricTable({ data }) {
  const metrics = data?.metrics || [];
  const rows = data?.rows || [];
  const totals = data?.totals || {};
  const preserveOrder = !!data?.preserve_order;
  const [sortKey, setSortKey] = useState(metrics[0]?.key || 'm0');
  const [sortDir, setSortDir] = useState('desc');

  const sorted = preserveOrder ? rows : [...rows].sort((a, b) => {
    const av = Number(a[sortKey]) || 0;
    const bv = Number(b[sortKey]) || 0;
    return sortDir === 'asc' ? av - bv : bv - av;
  });

  const toggleSort = (key) => {
    if (preserveOrder) return;
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const pctOf = (key, agg, value) => {
    if (agg === 'avg') return null;
    const t = Number(totals[key]);
    if (!t || !Number.isFinite(t) || t <= 0) return null;
    const v = Number(value);
    if (!Number.isFinite(v)) return null;
    return (v / t) * 100;
  };

  // avg now carries a real grand-total (overall average) from the backend (Bug-026),
  // so it counts toward showing the totals row too.
  const hasTotalsRow = metrics.length > 0
    && metrics.some((m) => !m.no_total)
    && Object.keys(totals).length > 0;

  return (
    <TableContainer maxH="420px" overflowY="auto">
      <Table size="sm" variant="simple">
        <Thead position="sticky" top={0} bg="white" zIndex={1}>
          <Tr>
            <Th>#</Th>
            <Th>Label</Th>
            {metrics.map((m) => (
              <Th key={m.key} isNumeric cursor={preserveOrder ? 'default' : 'pointer'}
                onClick={() => toggleSort(m.key)} _hover={{ bg: preserveOrder ? 'transparent' : 'gray.50' }}>
                <HStack justify="flex-end" spacing={1}>
                  <Text>{m.label}</Text>
                  {!preserveOrder && sortKey === m.key && <Icon as={sortDir === 'asc' ? FiChevronUp : FiChevronDown} boxSize={3} />}
                </HStack>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {sorted.map((r, i) => (
            <Tr key={`${r.label}-${i}`}>
              <Td fontSize="xs" color="gray.500">{i + 1}</Td>
              <Td fontSize="xs">{r.label == null || r.label === '' ? <em>—</em> : String(r.label)}</Td>
              {metrics.map((m) => {
                const pct = m.no_total ? null : pctOf(m.key, m.agg, r[m.key]);
                return (
                  <Td key={m.key} fontSize="xs" isNumeric fontWeight={m.key === sortKey ? 'semibold' : 'normal'}>
                    {fmtCompact(r[m.key])}
                    {pct != null && (
                      <Text as="span" fontSize="2xs" color="gray.500" ml={1.5}>
                        ({pct >= 10 ? pct.toFixed(1) : pct.toFixed(2)}%)
                      </Text>
                    )}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </Tbody>
        {hasTotalsRow && (
          <Tbody>
            <Tr bg="gray.50" borderTopWidth="2px" borderColor="gray.200">
              <Td fontSize="xs" color="gray.500" fontWeight="semibold">∑</Td>
              <Td fontSize="xs" fontWeight="semibold" color="gray.700">Total</Td>
              {metrics.map((m) => (
                <Td key={m.key} fontSize="xs" isNumeric fontWeight="semibold" color="gray.700">
                  {m.no_total ? '—' : fmtCompact(totals[m.key])}
                </Td>
              ))}
            </Tr>
          </Tbody>
        )}
      </Table>
    </TableContainer>
  );
}

// Grid of stat cards — one card per row, one column per metric.
// Used when backend hints display='cards' (sweep / attribution narrative tiles).
function MultiMetricCards({ data }) {
  const metrics = data?.metrics || [];
  const rows = data?.rows || [];
  if (rows.length === 0) return <Text fontSize="sm" color="gray.500">No rows.</Text>;
  return (
    <Box maxH="420px" overflowY="auto">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
        {rows.map((r, i) => (
          <Box key={`${r.label}-${i}`} p={3} borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="white">
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" mb={2}>
              {r.label == null || r.label === '' ? '—' : String(r.label)}
            </Text>
            <SimpleGrid columns={metrics.length > 2 ? 3 : metrics.length} spacing={2}>
              {metrics.map((m) => (
                <Stat key={m.key} size="sm">
                  <StatLabel fontSize="2xs" color="gray.500" noOfLines={2}>{m.label}</StatLabel>
                  <StatNumber fontSize="md" color="gray.800">{fmtCompact(r[m.key])}</StatNumber>
                </Stat>
              ))}
            </SimpleGrid>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  );
}

function SingleMetricBar({ data }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !data?.rows) return;
    const preserve = !!data.preserve_order;
    const isPct = data.value_format === 'pct';
    const isVertical = data.orientation === 'vertical';
    const rows = preserve ? data.rows.slice() : data.rows.slice().sort((a, b) => Number(b.v) - Number(a.v));
    const cats = rows.map((r) => (r.label == null || r.label === '' ? '—' : String(r.label).slice(0, 60)));
    const vals = rows.map((r) => Number(r.v));
    const negColor = '#ef4444'; // coral/red — only used when pct values dip below 0
    const baseColor = PRIMARY_BG;
    const points = vals.map((v) => ({ y: v, color: isPct && v < 0 ? negColor : baseColor }));
    Highcharts.chart(ref.current, {
      chart: {
        type: isVertical ? 'column' : 'bar',
        height: Math.max(180, cats.length * 28 + 60),
        spacing: [8, 8, 8, 8],
      },
      title: { text: undefined }, credits: { enabled: false }, legend: { enabled: false },
      xAxis: {
        categories: cats,
        title: { text: data.x_label || undefined, style: { fontSize: '10px', color: '#6b7280' } },
        labels: { style: { fontSize: '11px' } },
      },
      yAxis: {
        title: { text: data.y_label || (isPct ? 'Drop-off %' : 'Count'), style: { fontSize: '10px', color: '#6b7280' } },
        labels: {
          style: { fontSize: '10px' },
          formatter() { return isPct ? `${this.value}%` : this.value.toLocaleString(); },
        },
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            style: { fontSize: '10px' },
            formatter() { return isPct ? `${this.y.toFixed(1)}%` : this.y.toLocaleString(); },
          },
        },
      },
      tooltip: {
        pointFormatter() {
          return isPct
            ? `<b>${this.y.toFixed(1)}%</b>`
            : `<b>${this.y.toLocaleString()}</b>`;
        },
      },
      series: [{ name: isPct ? 'Drop-off %' : 'Count', data: points }],
    });
  }, [data]);
  return <div ref={ref} style={{ width: '100%' }} />;
}

// Volume bars (column) + drop-off line (spline) on a dual y-axis.
function ComboFunnelChart({ data }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !Array.isArray(data?.rows)) return;
    const rows = data.rows;
    const cats = rows.map((r) => String(r.label).slice(0, 60));
    const volume = rows.map((r) => Number(r.volume) || 0);
    const drop = rows.map((r) => Number(r.drop_pct) || 0);
    Highcharts.chart(ref.current, {
      chart: { height: 360, spacing: [10, 10, 10, 10] },
      title: { text: undefined }, credits: { enabled: false },
      xAxis: { categories: cats, labels: { style: { fontSize: '11px' } } },
      yAxis: [
        { title: { text: 'Volume', style: { fontSize: '10px' } }, labels: { style: { fontSize: '10px' } } },
        { title: { text: 'Drop-off %', style: { fontSize: '10px' } }, labels: { format: '{value}%', style: { fontSize: '10px' } }, opposite: true, min: 0 },
      ],
      legend: { itemStyle: { fontSize: '11px' } },
      tooltip: { shared: true },
      series: [
        { type: 'column', name: 'Volume', data: volume, color: PRIMARY_BG, yAxis: 0,
          dataLabels: { enabled: true, style: { fontSize: '10px' }, formatter() { return this.y.toLocaleString(); } } },
        { type: 'spline', name: 'Drop-off %', data: drop, color: '#ef4444', yAxis: 1,
          dataLabels: { enabled: true, style: { fontSize: '10px' }, format: '{y:.1f}%' } },
      ],
    });
  }, [data]);
  return <div ref={ref} style={{ width: '100%', minHeight: 360 }} />;
}

// Pie / Doughnut — categorical proportions from the same {label, v} breakdown rows.
function SingleMetricPie({ data, doughnut }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !data?.rows) return;
    const rows = data.rows.slice().sort((a, b) => Number(b.v) - Number(a.v));
    const points = rows.map((r) => ({
      name: (r.label == null || r.label === '') ? '—' : String(r.label).slice(0, 60),
      y: Number(r.v),
    }));
    const palette = [PRIMARY_BG, '#38a169', '#dd6b20', '#805ad5', '#d53f8c', '#319795', '#e53e3e', '#718096', '#d69e2e', PRIMARY_BG_HOVER];
    Highcharts.chart(ref.current, {
      chart: { type: 'pie', height: 320, spacing: [8, 8, 8, 8] },
      title: { text: undefined }, credits: { enabled: false }, colors: palette,
      tooltip: { pointFormat: '<b>{point.y:,.0f}</b> ({point.percentage:.1f}%)' },
      plotOptions: {
        pie: {
          innerSize: doughnut ? '55%' : 0,
          dataLabels: {
            enabled: true,
            style: { fontSize: '11px', textOutline: 'none' },
            format: '{point.name}: {point.percentage:.1f}%',
          },
        },
      },
      series: [{ name: 'Share', data: points }],
    });
  }, [data, doughnut]);
  return <div ref={ref} style={{ width: '100%', minHeight: 320 }} />;
}

export default function BreakdownBar({ data }) {
  if (!data) return null;
  if (data.chart === 'combo') return <ComboFunnelChart data={data} />;
  if (data.chart === 'pie' || data.chart === 'doughnut') return <SingleMetricPie data={data} doughnut={data.chart === 'doughnut'} />;
  if (data.chart === 'table' && Array.isArray(data.metrics) && data.metrics.length > 0) {
    if (data.display === 'cards') return <MultiMetricCards data={data} />;
    return <MultiMetricTable data={data} />;
  }
  return <SingleMetricBar data={data} />;
}
